// Any file type may be uploaded (e.g. .fm5/.fmt/.fmd label formats) EXCEPT the
// executable / script types below. Must match the block-list in the backend
// (Helper/UploadSecurity.cs); the backend check is the one that really protects.
export const BLOCKED_UPLOAD_GROUPS: {
  label: string;
  reason: string;
  extensions: string[];
}[] = [
  {
    label: 'Programs & installers',
    reason: 'Program file',
    extensions: [
      'exe', 'com', 'scr', 'pif', 'cpl', 'msi', 'msp', 'mst', 'msc',
      'dll', 'sys', 'drv', 'ocx', 'gadget', 'application',
      'appx', 'appxbundle', 'msix', 'msixbundle',
    ],
  },
  {
    label: 'Scripts & shortcuts',
    reason: 'Script file',
    extensions: [
      'bat', 'cmd', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh', 'ws', 'wsc', 'hta',
      'ps1', 'ps1xml', 'ps2', 'ps2xml', 'psc1', 'psc2', 'psm1', 'psd1',
      'reg', 'inf', 'scf', 'lnk', 'url',
    ],
  },
  {
    // Dangerous if the upload folder is ever served by the web server
    label: 'Server pages',
    reason: 'Server script',
    extensions: [
      'asp', 'aspx', 'asmx', 'ashx', 'axd', 'cshtml', 'vbhtml', 'config',
      'php', 'jsp',
    ],
  },
  {
    label: 'Other platforms',
    reason: 'Program file',
    extensions: ['jar', 'sh', 'bash', 'apk', 'app', 'dmg', 'pkg', 'deb', 'rpm', 'run'],
  },
];

export const BLOCKED_UPLOAD_EXTENSIONS = BLOCKED_UPLOAD_GROUPS.flatMap(
  (g) => g.extensions,
);

export const MAX_UPLOAD_SIZE_MB = 50;

export interface RejectedUpload {
  name: string;
  reason: string;
}

/**
 * Lower-case extension of a file name. Trailing dots/spaces are stripped first
 * because Windows drops them when saving ("evil.exe." is stored as "evil.exe").
 */
const getExtension = (name: string): string => {
  const trimmed = name.replace(/[.\s]+$/, '');
  const dot = trimmed.lastIndexOf('.');
  return dot === -1 ? '' : trimmed.slice(dot + 1).toLowerCase();
};

/** Short reason ("Program file", "Over 50 MB") when the file is rejected, otherwise null. */
export const getUploadRejectReason = (file: File): string | null => {
  const ext = getExtension(file?.name || '');
  const group = ext
    ? BLOCKED_UPLOAD_GROUPS.find((g) => g.extensions.includes(ext))
    : undefined;
  if (group) return `${group.reason} (.${ext})`;
  if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
    return `Over ${MAX_UPLOAD_SIZE_MB} MB`;
  }
  return null;
};

/** Full sentence error when the file can't be uploaded, otherwise null. */
export const getUploadFileError = (file: File): string | null => {
  const reason = getUploadRejectReason(file);
  return reason ? `"${file?.name || ''}" can't be uploaded: ${reason}.` : null;
};

/** Splits a selection into uploadable files and rejected ones (with reasons). */
export const validateUploadFiles = (
  files: FileList | File[],
): { valid: File[]; rejected: RejectedUpload[] } => {
  const valid: File[] = [];
  const rejected: RejectedUpload[] = [];
  Array.from(files as ArrayLike<File>).forEach((file) => {
    const reason = getUploadRejectReason(file);
    if (reason) rejected.push({ name: file.name, reason });
    else valid.push(file);
  });
  return { valid, rejected };
};

// File names come from the user's disk, so they must never be injected as raw HTML
const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fireUploadPopup = async (title: string, html: string, icon?: 'error') => {
  // sweetalert2 only downloaded when an alert actually needs to fire
  const { default: Swal } = await import('sweetalert2');
  Swal.fire({
    background: 'var(--app-surface)',
    color: 'var(--app-text)',
    customClass: { container: 'my-swal', popup: 'upload-swal' },
    title,
    html,
    icon,
    confirmButtonColor: 'var(--gallop-bg-primary)',
  });
};

/** Popup listing each rejected file and why; accepted files are shown as ready. */
export const showUploadRejections = (
  rejected: RejectedUpload[],
  accepted: File[] = [],
) => {
  if (!rejected.length) return;
  const row = (ok: boolean, name: string, reason: string) =>
    `<li class="upload-swal__row ${ok ? 'is-ok' : 'is-blocked'}">
       <span class="upload-swal__icon">${ok ? '&#10004;' : '&#10006;'}</span>
       <span class="upload-swal__name" title="${escapeHtml(name)}">${escapeHtml(name)}</span>
       <span class="upload-swal__reason">${escapeHtml(reason)}</span>
     </li>`;
  const rows = [
    ...rejected.map((r) => row(false, r.name, r.reason)),
    ...accepted.map((f) => row(true, f.name, 'Ready to upload')),
  ].join('');
  const count = rejected.length;
  fireUploadPopup(
    count === 1 ? "1 file can't be uploaded" : `${count} files can't be uploaded`,
    `<ul class="upload-swal__list">${rows}</ul>
     <p class="upload-swal__note">Executable and script files are blocked for security.
     Files must be ${MAX_UPLOAD_SIZE_MB} MB or smaller.</p>`,
    'error',
  );
};

/** Popup with every blocked extension, grouped by category. */
export const showBlockedFileTypes = () => {
  const groups = BLOCKED_UPLOAD_GROUPS.map(
    (g) =>
      `<div class="upload-swal__group">
         <div class="upload-swal__group-label">${g.label}</div>
         <div class="upload-swal__chips">${g.extensions
           .map((ext) => `<span class="upload-swal__chip">.${ext}</span>`)
           .join('')}</div>
       </div>`,
  ).join('');
  fireUploadPopup(
    'Blocked file types',
    `<p class="upload-swal__note">All other file types can be uploaded
     (max ${MAX_UPLOAD_SIZE_MB} MB per file).</p>${groups}`,
  );
};
